/**
 * DailyPanel — Daily Quests + No-Spend Day tracker
 * Appears on the Home dashboard. Gives users a daily reason to open the app.
 */
import React, { useState } from 'react';
import { getDailyQuests, getNoSpendStreak } from '../utils';
import { hitBoss } from '../api';

export default function DailyPanel({ state, updateState, showToast }) {
  const today = new Date().toISOString().slice(0, 10);
  const quests = getDailyQuests(state);
  const noSpendToday = (state.noSpendDays || []).includes(today);
  const noSpendStreak = getNoSpendStreak(state);
  const allDone = quests.every(q => q.done);
  const [justCompleted, setJustCompleted] = useState(null);

  const markQuestDone = (questId, xp) => {
    const prev = state.completedQuests || {};
    const today = new Date().toISOString().slice(0, 10);
    const todayList = prev[today] || [];
    if (todayList.includes(questId)) return;
    updateState({ completedQuests: { ...prev, [today]: [...todayList, questId] } });
    setJustCompleted(questId);
    showToast(`+${xp} XP — quest complete! ⚡`);
    // Deal damage to the current boss
    hitBoss(xp).catch(() => {});
    setTimeout(() => setJustCompleted(null), 1200);
  };

  const toggleNoSpend = () => {
    const days = state.noSpendDays || [];
    if (noSpendToday) {
      updateState({ noSpendDays: days.filter(d => d !== today) });
    } else {
      updateState({ noSpendDays: [...days, today] });
      showToast('No-spend day logged! 🛑 +25 XP');
      hitBoss(25).catch(() => {});
      // Auto-complete the no-spend quest
      const prev = state.completedQuests || {};
      const todayList = prev[today] || [];
      if (!todayList.includes('no_spend')) {
        updateState({ noSpendDays: [...days, today], completedQuests: { ...prev, [today]: [...todayList, 'no_spend'] } });
      }
    }
  };

  const doneCount = quests.filter(q => q.done).length;

  return (
    <div className="card cp" style={{ marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div className="st" style={{ marginBottom: 0 }}>⚡ Daily Quests</div>
          <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>
            {doneCount}/{quests.length} complete · resets at midnight
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 900, color: allDone ? 'var(--grn)' : 'var(--ylw)' }}>
          {allDone ? '✓ All Done!' : `${doneCount}/${quests.length}`}
        </div>
      </div>

      {/* XP progress bar */}
      <div style={{ height: 6, background: 'var(--surf2)', borderRadius: 999, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ height: '100%', width: (doneCount / Math.max(1, quests.length) * 100) + '%', background: 'var(--ylw)', borderRadius: 999, transition: 'width .4s' }} />
      </div>

      {/* Quests */}
      {quests.map(q => (
        <div
          key={q.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
            borderBottom: '1px solid var(--bdr)',
            opacity: q.done ? 0.55 : 1,
            transition: 'opacity .3s',
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: q.done ? 'rgba(107,203,119,.2)' : 'var(--surf2)',
            border: `2px solid ${q.done ? 'var(--grn)' : 'var(--bdr)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            animation: justCompleted === q.id ? 'level-pop .4s' : 'none',
          }}>
            {q.done ? '✓' : q.em}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, textDecoration: q.done ? 'line-through' : 'none', color: q.done ? 'var(--mut)' : 'var(--txt)' }}>
              {q.label}
            </div>
            <div style={{ fontSize: 10, color: 'var(--ylw)', fontWeight: 700 }}>+{q.xp} XP</div>
          </div>
          {!q.done && q.id !== 'log_spend' && q.id !== 'log_income' && q.id !== 'update_bank' && (
            <button
              onClick={() => markQuestDone(q.id, q.xp)}
              style={{ padding: '5px 12px', borderRadius: 20, background: 'rgba(255,217,61,.12)', border: '1.5px solid rgba(255,217,61,.4)', color: 'var(--ylw)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >
              Done
            </button>
          )}
        </div>
      ))}

      {/* No-Spend Day */}
      <div style={{ marginTop: 14, padding: '12px', background: noSpendToday ? 'rgba(107,203,119,.1)' : 'var(--surf2)', borderRadius: 12, border: `1px solid ${noSpendToday ? 'rgba(107,203,119,.4)' : 'var(--bdr)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28 }}>🛑</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>No-Spend Day</div>
            <div style={{ fontSize: 10, color: 'var(--mut)' }}>
              {noSpendToday ? '✓ Logged for today' : 'Tap to mark — no purchases today'}
              {noSpendStreak > 0 && <span style={{ color: 'var(--grn)', marginLeft: 8, fontWeight: 700 }}>🔥 {noSpendStreak} day streak!</span>}
            </div>
          </div>
          <button
            onClick={toggleNoSpend}
            style={{
              padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: 'pointer', border: 'none',
              background: noSpendToday ? 'var(--grn)' : 'var(--ylw)',
              color: '#000',
            }}
          >
            {noSpendToday ? 'Undo' : 'Mark'}
          </button>
        </div>
      </div>
    </div>
  );
}
